import API from "../http";

const DEMO_KG_ACCESS = ["VIEW", "EDIT", "INSERT", "DELETE"];

function normKey(k) {
  return typeof k === "string" ? k.replace(/^\//, "") : "";
}

/** Батьківський модуль закладу: у children є маршрути institution/{id}/… */
export function isInstitutionParentModule(section) {
  if (!section?.children?.length) return false;
  return section.children.some((c) => /^institution\/\d+\//.test(normKey(c?.key)));
}

export function extractInstitutionIdFromKey(key) {
  const m = normKey(key).match(/^institution\/(\d+)\//);
  return m ? m[1] : null;
}

function getInstitutionIdFromSection(section) {
  for (const c of section?.children || []) {
    const id = extractInstitutionIdFromKey(c?.key);
    if (id) return id;
  }
  return null;
}

export function collectInstitutionIdsFromMenu(accessGroup) {
  const ids = new Set();
  for (const section of accessGroup || []) {
    for (const c of section?.children || []) {
      const kid = extractInstitutionIdFromKey(c?.key);
      if (kid) ids.add(kid);
    }
  }
  return ids;
}

function remapInstitutionPath(value, fromKgId, toKgId) {
  const s = String(value || "");
  if (!s) return s;
  return s
    .replace(new RegExp(`institution/${fromKgId}/`, "g"), `institution/${toKgId}/`)
    .replace(new RegExp(`^institution/${fromKgId}$`), `institution/${toKgId}`);
}

export function cloneInstitutionMenuSection(template, fromKgId, toKgId, displayName) {
  const children = (template.children || []).map((child) => {
    const key = normKey(child.key);
    const moduleId = String(child.module_id || key);
    return {
      ...child,
      key: remapInstitutionPath(key, fromKgId, toKgId),
      module_id: remapInstitutionPath(moduleId, fromKgId, toKgId),
      access: Array.isArray(child.access) && child.access.length
        ? [...child.access]
        : [...DEMO_KG_ACCESS],
    };
  });

  return {
    ...template,
    module_id: `demo-institution-parent-${toKgId}`,
    module_name: displayName,
    children,
  };
}

/** Додає «Про нас» для кожного institution/{id}/… у children. */
export function injectDemoInstitutionAboutMenuItems(section) {
  if (!section || !Array.isArray(section.children) || section.children.length === 0) {
    return section;
  }
  const ids = new Set();
  for (const c of section.children) {
    const match = normKey(c?.key).match(/^institution\/(\d+)\//);
    if (match) ids.add(match[1]);
  }
  if (ids.size === 0) return section;
  const existing = new Set(section.children.map((c) => normKey(c.key)));
  const extras = [];
  for (const kid of ids) {
    const key = `institution/${kid}/about`;
    if (existing.has(key)) continue;
    extras.push({
      key,
      module_name: "Про нас",
      module_id: `institution/${kid}/about`,
      access: [...DEMO_KG_ACCESS],
    });
    existing.add(key);
  }
  if (!extras.length) return section;
  return { ...section, children: [...section.children, ...extras] };
}

export async function fetchDemoInstitutionsList() {
  try {
    const res = await API.get("api/institution/institutions");
    const data = res?.data;
    return Array.isArray(data) ? data : [];
  } catch (e) {
    return [];
  }
}

/**
 * Для демо: додати в меню заклади з довідника, яких немає в access_group з auth.
 * Шаблон підменю клонується з першого наявного модуля закладу.
 */
export function expandDemoInstitutionMenu(accessGroup, institutions = []) {
  if (!Array.isArray(accessGroup) || !institutions.length) return accessGroup;

  const template = accessGroup.find(isInstitutionParentModule);
  if (!template) return accessGroup;

  const existingIds = collectInstitutionIdsFromMenu(accessGroup);
  const fromKgId =
    getInstitutionIdFromSection(template) ||
    [...existingIds][0] ||
    extractInstitutionIdFromKey(template.children?.[0]?.key);

  if (!fromKgId) return accessGroup;

  const sorted = [...institutions].sort((a, b) => Number(a.id) - Number(b.id));
  const extraSections = [];

  for (const kg of sorted) {
    const id = String(kg.id);
    if (existingIds.has(id)) continue;
    extraSections.push(
      cloneInstitutionMenuSection(
        template,
        fromKgId,
        id,
        kg.name || `Заклад ${id}`,
      ),
    );
  }

  if (!extraSections.length) return accessGroup;

  const result = [...accessGroup];
  let lastKgIdx = -1;
  for (let i = 0; i < result.length; i += 1) {
    if (isInstitutionParentModule(result[i])) lastKgIdx = i;
  }
  result.splice(lastKgIdx + 1, 0, ...extraSections);
  return result;
}
